import { useCallback } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase.js';

const useFirebaseCache = () => {
  // Check if cache should be refreshed based on timestamp
  const shouldRefreshCache = useCallback((cacheKey, expiryMinutes = 30) => {
    const cachedTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);
    if (!cachedTimestamp) return true;
    
    const now = new Date().getTime();
    const cachedTime = parseInt(cachedTimestamp, 10);
    const minutesPassed = (now - cachedTime) / (1000 * 60);
    
    return minutesPassed > expiryMinutes;
  }, []);
  
  // Check if a collection has changed since last fetch
  const hasCollectionChanged = useCallback(async (collectionName, lastFetchTime) => {
    try {
      // For user collections, use a simpler approach to avoid security rule issues
      if (collectionName.startsWith('users/')) {
        // For user collections, just check if any documents exist
        // This is safer than ordering by updatedAt which might not exist on all documents
        const metadataQuery = query(
          collection(db, collectionName),
          limit(1)
        );
        // We just care if we can access it, not about the contents
        await getDocs(metadataQuery);
        
        // If we can successfully fetch at least one document, assume no changes
        // for better performance. Cache expiration will handle updates eventually.
        return false;
      } else {
        // For non-user collections, continue using the timestamp-based approach
        const metadataQuery = query(
          collection(db, collectionName), 
          orderBy('updatedAt', 'desc'), 
          limit(1)
        );
        const snapshot = await getDocs(metadataQuery);
        
        if (snapshot.empty) return false;
        
        const mostRecentUpdate = snapshot.docs[0].data().updatedAt;
        if (!mostRecentUpdate) return true;
        
        const mostRecentUpdateTime = mostRecentUpdate.toDate ? 
          mostRecentUpdate.toDate().getTime() : 
          new Date(mostRecentUpdate).getTime();
        
        return mostRecentUpdateTime > lastFetchTime;
      }
    } catch (error) {
      console.error('Error checking collection changes:', error);
      return false; // If we can't check, assume no changes and use cache
    }
  }, []);

  // Get cached data from localStorage
  const getCachedData = useCallback((cacheKey, defaultValue = []) => {
    try {
      const cachedData = JSON.parse(localStorage.getItem(cacheKey) || JSON.stringify(defaultValue));
      return cachedData;
    } catch (error) {
      console.error(`Error retrieving cached data for ${cacheKey}:`, error);
      return defaultValue;
    }
  }, []);

  // Set cached data with timestamp
  const setCachedData = useCallback((cacheKey, data) => {
    try {
      localStorage.setItem(cacheKey, JSON.stringify(data));
      localStorage.setItem(`${cacheKey}_timestamp`, new Date().getTime().toString());
    } catch (error) {
      console.error(`Error setting cached data for ${cacheKey}:`, error);
    }
  }, []);

  // Clear cache for a specific key
  const clearCache = useCallback((cacheKey) => {
    try {
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(`${cacheKey}_timestamp`);
    } catch (error) {
      console.error(`Error clearing cache for ${cacheKey}:`, error);
    }
  }, []);

  return {
    shouldRefreshCache,
    hasCollectionChanged,
    getCachedData,
    setCachedData,
    clearCache
  };
};

export default useFirebaseCache; 